import { CalendarDays } from "lucide-react";
import { useAppointments } from "@/api/queries";
import type { AppointmentFilters, AppointmentRow } from "@/api/types";
import { StatusBadge } from "@/components/feedback/status-badge";
import { OpportunityLink, OrgLink, StoreLink } from "@/components/navigation/entity-links";
import { AdminTable, type Column } from "@/components/tables/admin-table";
import { type ResourceTableProps, tablePaging } from "@/components/tables/types";
import { formatShortDateTime } from "@/lib/dates";
import { APPOINTMENT } from "@/lib/status";

export function AppointmentsTable({ query, onChange, toolbar, hide = [], filtered }: ResourceTableProps<AppointmentFilters>) {
  const q = useAppointments(query);
  const columns: Column<AppointmentRow>[] = [
    {
      id: "when",
      header: "When",
      sortKey: "startsAt",
      alwaysVisible: true,
      cell: (a) => <span className="tabular">{formatShortDateTime(a.startsAt)}</span>,
    },
    {
      id: "organization",
      header: "Organization",
      cell: (a) => <OrgLink org={a.organization} />,
    },
    {
      id: "store",
      header: "Store",
      cell: (a) => <StoreLink store={a.store} />,
    },
    { id: "seller", header: "Seller", cell: (a) => a.seller?.name ?? "—" },
    // { id: "vehicle", header: "Vehicle", cell: (a) => a.vehicle?.title ?? "—" },
    {
      id: "vehicle",
      header: "Vehicle",
      cell: (a) => <OpportunityLink id={a.opportunityId} label={a.vehicle?.title ?? "—"} />,
    },
    {
      id: "status",
      header: "Status",
      cell: (a) => <StatusBadge config={APPOINTMENT[a.status]} />,
    },
    {
      id: "source",
      header: "Booked by",
      cell: (a) => (a.source === "AI" ? "Rogue" : a.source === "USER" ? "Staff" : "System"),
    },
  ];
  return (
    <AdminTable
      id="appointments"
      label="Appointments"
      resourceName="appointments"
      columns={columns.filter((c) => !hide.includes(c.id))}
      rows={q.data?.items}
      rowKey={(a) => a.id}
      isPending={q.isPending}
      isFetching={q.isFetching}
      error={q.error}
      onRetry={() => void q.refetch()}
      {...tablePaging(query, q.data?.total, onChange)}
      toolbar={toolbar}
      empty={
        filtered
          ? {
              icon: CalendarDays,
              title: "No appointments match",
              description: "Try another date window or status.",
            }
          : {
              icon: CalendarDays,
              title: "No appointments",
              description: "Appointments appear when sellers agree to bring vehicles in.",
            }
      }
    />
  );
}
